import { ReactNode, useMemo } from 'react';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import {
	FGAInternalRole,
	Permission,
	PermissionWithOwner,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { PermissionRoleWithOwnerEnum } from '@dealstack/shared/lib/generated/models/PermissionRoleWithOwnerEnum';

/**
 * Hook that checks if a user has permission based on Redux store data
 * @param params Permission parameters (resourceType, resourceId, relation)
 * @returns Object containing permission status and loading state
 */
export const usePermissionRequest = (params: Partial<Omit<PermissionWithOwner, 'isImplicit'>>) => {
	const { resourceType, resourceId, permissionRole } = params;

	const userData = useAppSelector((state) => state.user.data);
	const isLoading = useAppSelector((state) => state.user.isLoading);

	const hasPermission = useMemo(() => {
		if (!userData || !userData.permissions) {
			return false;
		}

		return userData.permissions.some((permission) => {
			return (
				// FGAInternalRole is intended for internal use only and should not be exposed to end users.
				// This type casting is necessary to allow Dealstack support to view all components.
				permission.permissionRole ===
					(FGAInternalRole.DEALSTACK_SUPPORT as unknown as PermissionRoleWithOwnerEnum) ||
				(permission.resourceType === resourceType &&
					permission.resourceId === resourceId &&
					permission.permissionRole === permissionRole)
			);
		});
	}, [userData, resourceType, resourceId, permissionRole]);

	return {
		hasPermission,
		isLoading,
		error: null,
	};
};

interface Props extends Omit<Permission, 'isImplicit'> {
	children?: ReactNode | ((hasPermission: boolean) => JSX.Element);
	disableOnly?: boolean;
	loadingFallback?: ReactNode;
}

/**
 * PermissionWrapper component
 * Renders or hides/disables children based on user permissions from Redux store
 *
 * @example
 * // To hide content when no permission:
 * <PermissionWrapper resourceType={WarrantResourceType.Deal} resourceId="123" relation={WarrantRelation.Viewer}>
 *   <SomeComponent />
 * </PermissionWrapper>
 *
 * @example
 * // To disable content when no permission:
 * <PermissionWrapper resourceType={WarrantResourceType.Deal} resourceId="123" relation={WarrantRelation.Admin} disableOnly={true}>
 *   <Button>Admin Action</Button>
 * </PermissionWrapper>
 *
 * @example
 * // Using function as children for more control:
 * <PermissionWrapper resourceType={WarrantResourceType.Deal} resourceId="123" relation={WarrantRelation.Editor}>
 *   {(hasPermission) => hasPermission ? <EditButton /> : <ViewOnlyBadge />}
 * </PermissionWrapper>
 */
export const PermissionWrapper = ({
	children,
	resourceType,
	resourceId,
	permissionRole,
	disableOnly = false,
	loadingFallback = null,
}: Props): JSX.Element => {
	const { hasPermission, isLoading } = usePermissionRequest({
		resourceType,
		resourceId,
		permissionRole,
	});

	// While loading, render the loading fallback or nothing
	if (isLoading) {
		return <>{loadingFallback}</>;
	}

	// Function as child pattern
	if (typeof children === 'function') {
		return children(hasPermission);
	}

	// If no permission and not disableOnly, hide the children
	if (!hasPermission && !disableOnly) {
		return <></>;
	}

	// If no permission and disableOnly, render children in disabled state
	if (!hasPermission && disableOnly) {
		return <div style={{ pointerEvents: 'none', opacity: 0.6 }}>{children}</div>;
	}

	// Default case: render children normally when the user has permission
	if (hasPermission) {
		return <>{children}</>;
	}

	// This should never happen given the previous conditions
	return <></>;
};
